import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { ADMIN_NAV } from '../../lib/adminRoutes';
import { readAdminTheme, setAdminTheme } from '../admin/AdminThemeScope';
import { countPendingLessonReviews } from '../../repos/majorHighReviewRepo';

const ROLE_LABELS = { owner: 'Proprietário', admin: 'Administrador', manager: 'Gestão', instructor: 'Instrutor', student: 'Estudante' };

const sectionOfPath = pathname => ADMIN_NAV.find(group => group.items.some(item => item.path.split('?')[0] === pathname))?.id;

const AdminSidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [expandedSections, setExpandedSections] = useState(() => ['overview', 'learning', sectionOfPath(location?.pathname)].filter(Boolean));
  const [pendingLessonReviews, setPendingLessonReviews] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(readAdminTheme);

  useEffect(() => {
    countPendingLessonReviews().then(setPendingLessonReviews).catch(() => setPendingLessonReviews(0));
    setMobileOpen(false);
    const section = sectionOfPath(location?.pathname);
    if (section) setExpandedSections(current => (current.includes(section) ? current : [...current, section]));
  }, [location?.pathname]);

  // Below 1100px the expanded rail squeezes every page into sideways scroll. Pages own
  // the collapsed state, so the sidebar asks once on arrival instead of editing each one.
  useEffect(() => {
    if (window.innerWidth < 1100 && !isCollapsed) onToggleCollapse?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = event => { if (event.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const isActive = (path) => {
    const [menuPath, menuQuery] = path.split('?');
    if (location?.pathname !== menuPath) return false;
    const currentQuery = location?.search?.substring(1);
    if (!menuQuery) return !currentQuery || currentQuery === 'tab=templates' || !currentQuery.includes('tab=');
    return currentQuery === menuQuery;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => (prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]));
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setAdminTheme(next);
    setTheme(next);
  };

  const person = session?.profile?.name || session?.systemUser?.name || session?.user?.email || '';
  const role = ROLE_LABELS[session?.userTenant?.role] || '';
  const compact = isCollapsed && !mobileOpen;

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed left-3 bottom-3 z-100 inline-flex items-center gap-2 h-11 px-4 rounded-full bg-primary text-primary-foreground shadow-tier-2 text-sm font-medium"
      >
        <Icon name="Menu" size={18} aria-hidden="true" />Menu do admin
      </button>
      {mobileOpen && <div className="md:hidden fixed inset-0 z-100 bg-black/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />}
      <aside
        aria-label="Menu do admin"
        className={`fixed left-0 bottom-0 bg-card border-r border-border transition-all duration-300 z-100 ${mobileOpen ? 'block top-0 w-80 max-w-[85vw]' : 'hidden md:block top-16'} ${compact ? 'w-16' : 'md:w-72'}`}
      >
        <div className="flex flex-col h-full">
          <div className="p-2 border-b border-border flex justify-end md:justify-start">
            {mobileOpen ? (
              <button type="button" onClick={() => setMobileOpen(false)} className="h-11 px-3 inline-flex items-center gap-2 rounded-lg text-sm hover:bg-accent">
                <Icon name="X" size={18} aria-hidden="true" />Fechar
              </button>
            ) : (
              <button
                type="button"
                onClick={(event) => { event.preventDefault(); event.stopPropagation(); onToggleCollapse?.(); }}
                aria-label={compact ? 'Expandir menu' : 'Recolher menu'}
                className="w-full h-10 flex items-center px-2 rounded-lg text-sm text-muted-foreground transition-micro hover:bg-accent hover:text-accent-foreground"
              >
                <Icon name={compact ? 'ChevronRight' : 'ChevronLeft'} size={18} aria-hidden="true" />
                {!compact && <span className="ml-2">Recolher menu</span>}
              </button>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto p-2 space-y-1" aria-label="Áreas do admin">
            {ADMIN_NAV.map((section) => {
              const isExpanded = expandedSections.includes(section.id);
              const listId = `mh-nav-${section.id}`;
              return (
                <div key={section.id}>
                  {!compact && (
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      aria-expanded={isExpanded}
                      aria-controls={listId}
                      className="w-full h-9 flex items-center justify-between px-2 mt-1 rounded-lg hover:bg-accent"
                    >
                      <span className="mh-admin-nav-label">{section.label}</span>
                      <Icon name="ChevronDown" size={14} className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                  )}
                  {(isExpanded || compact) && (
                    <ul id={listId} className="space-y-0.5">
                      {section.items.map((item) => {
                        const active = isActive(item.path);
                        const count = item.badge === 'lessonReviews' ? pendingLessonReviews : 0;
                        return (
                          <li key={item.path}>
                            <button
                              type="button"
                              disabled={item.disabled}
                              aria-current={active ? 'page' : undefined}
                              title={compact ? item.label : undefined}
                              aria-label={compact ? item.label : undefined}
                              onClick={() => navigate(item.path)}
                              className={`mh-admin-nav-item w-full flex items-center gap-2.5 px-2 rounded-lg text-sm text-left transition-micro ${compact ? 'justify-center' : ''} ${item.disabled ? 'cursor-not-allowed opacity-60' : active ? '' : 'hover:bg-accent hover:text-accent-foreground'}`}
                            >
                              <Icon name={item.icon} size={16} className="shrink-0 mh-admin-nav-soft" aria-hidden="true" />
                              {!compact && <span className="flex-1 min-w-0">{item.label}</span>}
                              {!compact && count > 0 && <span className="mh-admin-count">{count}</span>}
                              {!compact && item.disabled && <span className="mh-admin-pill">Em breve</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="p-3 border-t border-border space-y-2">
            {!compact && person && (
              <div className="min-w-0 px-1">
                <p className="text-sm font-medium truncate">{person}</p>
                {role && <p className="text-xs text-muted-foreground">{role}</p>}
              </div>
            )}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Usar tema claro no admin' : 'Usar tema escuro no admin'}
              title={compact ? (theme === 'dark' ? 'Tema claro' : 'Tema escuro') : undefined}
              className={`w-full h-10 flex items-center gap-2 px-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground ${compact ? 'justify-center' : ''}`}
            >
              <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={16} aria-hidden="true" />
              {!compact && <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
